"use client";

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "../form";

import * as S from "./styles";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterColumn: string;
  dict: { [key: string]: string };
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  handlePageChange: (page: number) => void;
  onFilterChange?: (value: string) => void;
  emptyStateComponent?: React.ReactNode;
  showEmptyStateOnlyWithoutSearch?: boolean;
  searchValue?: string;
  showSearch?: boolean;
  // totalRows: number;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  dict,
  filterColumn = "name",
  totalPages,
  currentPage,
  itemsPerPage,
  handlePageChange,
  onFilterChange,
  emptyStateComponent,
  showEmptyStateOnlyWithoutSearch = false,
  searchValue: externalSearchValue,
  showSearch = true,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [searchValue, setSearchValue] = React.useState<string>(
    externalSearchValue || "",
  );
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const table = useReactTable({
    data: data || [],
    columns,
    manualPagination: true,
    pageCount: totalPages,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: itemsPerPage,
      },
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({
              pageIndex: currentPage - 1,
              pageSize: itemsPerPage,
            })
          : updater;

      handlePageChange(newState.pageIndex + 1);
    },
  });

  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchValue(value);
      table.getColumn(filterColumn)?.setFilterValue(value);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (onFilterChange) {
        debounceTimeoutRef.current = setTimeout(() => {
          onFilterChange(value);
        }, 300);
      }
    },
    [table, filterColumn, onFilterChange],
  );

  return (
    <div className="w-full">
      <div className="flex flex-col space-y-4 sm:flex-row items-stretch sm:items-center py-1 gap-3 sm:gap-3.75">
        {showSearch && (
          <Input
            name="dynamicFilter"
            placeholder={dict.Search}
            value={searchValue}
            onChange={(event) => {
              const value = event.target.value;
              handleSearchChange(value);
            }}
            className="w-full sm:max-w-sm"
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full sm:w-auto sm:ml-auto">
              Colunas
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="capitalize"
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {column.id}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <S.HeaderTable key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </S.HeaderTable>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel()?.rows?.length ? (
              table.getRowModel()?.rows?.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row?.getVisibleCells()?.map((cell) => (
                    <TableCell key={cell?.id}>
                      {flexRender(
                        cell?.column?.columnDef?.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {(!data || data.length === 0) &&
                  !searchValue &&
                  showEmptyStateOnlyWithoutSearch &&
                  emptyStateComponent
                    ? emptyStateComponent
                    : "Sem resultados."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <S.FooterTable>
        <S.FirstBoxFooter className="flex-1 text-sm text-muted-foreground">
          {dict.Page} {currentPage} {dict.of} {totalPages} {dict.Pages}
        </S.FirstBoxFooter>
        <S.PaginationBox className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {dict.Previous}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {dict.Next}
          </Button>
        </S.PaginationBox>
      </S.FooterTable>
    </div>
  );
}
